import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { Materials } from '../render/Materials.js';
import { clamp, clamp01, TAU } from '../util/math.js';

/**
 * Procedural soldier: skeleton definition, skinned geometry builder and the
 * two-bone IK used to keep both hands welded to the weapon.
 *
 * Everything here runs once at init. The result is a small set of shared
 * BufferGeometries (one per squad variant) plus three shared materials, so a
 * spawned enemy costs three draw calls and a 20-bone skeleton — nothing else.
 *
 * Convention: the model faces -Z at yaw 0, matching Player._forward(), and its
 * origin sits between the boots so `root.position` is a feet position.
 * Every bone's local -Y axis points down its own limb in the bind pose, which
 * makes "positive X rotation swings the limb forward" true for every joint.
 */

// [name, parent, x, y, z] — parents always precede children.
const BONE_DEFS = [
  ['hips', null, 0, 0.975, 0],
  ['spine', 'hips', 0, 0.16, 0],
  ['chest', 'spine', 0, 0.21, 0],
  ['neck', 'chest', 0, 0.20, -0.005],
  ['head', 'neck', 0, 0.085, 0],
  ['clavL', 'chest', 0.055, 0.145, 0],
  ['armL', 'clavL', 0.145, -0.025, 0],
  ['foreL', 'armL', 0, -0.29, 0],
  ['handL', 'foreL', 0, -0.27, 0],
  ['clavR', 'chest', -0.055, 0.145, 0],
  ['armR', 'clavR', -0.145, -0.025, 0],
  ['foreR', 'armR', 0, -0.29, 0],
  ['handR', 'foreR', 0, -0.27, 0],
  ['thighL', 'hips', 0.105, -0.055, 0],
  ['shinL', 'thighL', 0, -0.44, 0],
  ['footL', 'shinL', 0, -0.42, 0],
  ['thighR', 'hips', -0.105, -0.055, 0],
  ['shinR', 'thighR', 0, -0.44, 0],
  ['footR', 'shinR', 0, -0.42, 0],
  ['weapon', 'chest', -0.085, 0.03, -0.20],
];

export const BONE = {};
BONE_DEFS.forEach((d, i) => { BONE[d[0]] = i; });
export const BONE_COUNT = BONE_DEFS.length;

export const ARM = { upper: 0.29, fore: 0.27 };
export const LEG = { thigh: 0.44, shin: 0.42 };

/** Weapon-local landmarks. The muzzle is what the fire raycast uses. */
export const WEAPON_LOCAL = {
  muzzle: new THREE.Vector3(0, 0.012, -0.50),
  gripRear: new THREE.Vector3(0, -0.045, 0.05),
  gripFront: new THREE.Vector3(0, -0.03, -0.22),
  eject: new THREE.Vector3(0.045, 0.02, -0.02),
};

// Vertex-colour tints, multiplied over the shared albedo of the base material.
// Three squads' worth of kit so a wave does not read as clones.
const PALETTES = [
  { // desert / tan
    uniform: [1.55, 1.34, 0.90], pouch: [1.18, 1.00, 0.68], glove: [0.40, 0.38, 0.36],
    boot: [0.62, 0.52, 0.44], skin: [1.90, 1.45, 1.05], helmet: [1.72, 1.48, 1.02],
    gear: [1.05, 0.92, 0.66], dark: [0.42, 0.42, 0.45], gun: [1.05, 1.02, 1.00],
    grip: [0.60, 0.58, 0.58], visor: [0.55, 0.95, 1.35], sole: [0.35, 0.35, 0.36],
  },
  { // olive drab
    uniform: [0.92, 1.10, 0.74], pouch: [0.72, 0.86, 0.58], glove: [0.36, 0.36, 0.34],
    boot: [0.45, 0.42, 0.38], skin: [1.75, 1.32, 0.98], helmet: [0.80, 0.92, 0.70],
    gear: [0.70, 0.82, 0.56], dark: [0.38, 0.40, 0.38], gun: [0.90, 0.92, 0.92],
    grip: [0.50, 0.52, 0.50], visor: [0.60, 1.05, 1.20], sole: [0.32, 0.32, 0.33],
  },
  { // urban grey
    uniform: [1.05, 1.08, 1.14], pouch: [0.82, 0.85, 0.92], glove: [0.34, 0.34, 0.38],
    boot: [0.42, 0.42, 0.46], skin: [1.82, 1.40, 1.10], helmet: [0.95, 0.98, 1.06],
    gear: [0.78, 0.82, 0.90], dark: [0.36, 0.37, 0.42], gun: [1.00, 1.00, 1.04],
    grip: [0.52, 0.52, 0.56], visor: [0.70, 1.15, 1.45], sole: [0.30, 0.30, 0.32],
  },
];

const MAT_CLOTH = 0, MAT_KIT = 1, MAT_HARD = 2;

// Reusable build-time scratch. None of this is touched after init().
const _v = new THREE.Vector3();
const _n = new THREE.Vector3();
const _m4a = new THREE.Matrix4();
const _m4b = new THREE.Matrix4();
const _nm3 = new THREE.Matrix3();
const _e = new THREE.Euler();
const _q = new THREE.Quaternion();

/**
 * Merges part geometries into one interleaved-by-attribute buffer with three
 * material groups. Skin weights come from the part's own bone plus optional
 * "soft" neighbours keyed on the vertex's height inside the bone's local
 * frame — that is what stops shoulders and knees from tearing open when the
 * joint rotates.
 */
class GeoAcc {
  constructor(groups) {
    this.pos = []; this.nor = []; this.uv = []; this.col = [];
    this.si = []; this.sw = [];
    this.tris = [];
    for (let i = 0; i < groups; i++) this.tris.push([]);
    this.count = 0;
  }

  add(geo, partMat, boneMat, boneIdx, soft, matIdx, tint) {
    const pos = geo.attributes.position;
    const nor = geo.attributes.normal;
    const uvA = geo.attributes.uv;
    const idx = geo.index;
    const base = this.count;

    _m4a.multiplyMatrices(boneMat, partMat);
    _nm3.getNormalMatrix(_m4a);

    for (let i = 0; i < pos.count; i++) {
      _v.fromBufferAttribute(pos, i).applyMatrix4(partMat);
      const yLocal = _v.y;
      _v.applyMatrix4(boneMat);
      this.pos.push(_v.x, _v.y, _v.z);

      _n.fromBufferAttribute(nor, i).applyMatrix3(_nm3).normalize();
      this.nor.push(_n.x, _n.y, _n.z);

      if (uvA) this.uv.push(uvA.getX(i), uvA.getY(i));
      else this.uv.push(0, 0);
      const pattern = Math.sin(_v.x * 51 + Math.sin(_v.y * 29) * 2.1) + Math.sin(_v.z * 43 - _v.y * 36);
      const tone = matIdx === MAT_CLOTH ? (pattern > 0.75 ? 0.70 : pattern < -0.7 ? 1.12 : 0.92) : 1;
      this.col.push(tint[0] * tone, tint[1] * tone, tint[2] * tone);

      // primary bone plus up to three soft neighbours
      let w1 = 0, w2 = 0, w3 = 0;
      let b1 = 0, b2 = 0, b3 = 0;
      if (soft) {
        for (let s = 0; s < soft.length && s < 3; s++) {
          const sd = soft[s];
          const w = 0.5 * clamp01(1 - Math.abs(yLocal - sd.y) / sd.span);
          if (s === 0) { b1 = sd.bone; w1 = w; }
          else if (s === 1) { b2 = sd.bone; w2 = w; }
          else { b3 = sd.bone; w3 = w; }
        }
      }
      const sum = w1 + w2 + w3;
      // never let the neighbours outvote the bone the part actually belongs to
      if (sum > 0.7) { const k = 0.7 / sum; w1 *= k; w2 *= k; w3 *= k; }
      const w0 = 1 - (w1 + w2 + w3);
      this.si.push(boneIdx, b1, b2, b3);
      this.sw.push(w0, w1, w2, w3);
      this.count++;
    }

    const tri = this.tris[matIdx];
    if (idx) for (let i = 0; i < idx.count; i++) tri.push(base + idx.getX(i));
    else for (let i = 0; i < pos.count; i++) tri.push(base + i);
  }

  build() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    const uv = new THREE.Float32BufferAttribute(this.uv, 2);
    g.setAttribute('uv', uv);
    g.setAttribute('uv1', uv);           // aoMap samples uv1 in three's glTF convention
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(this.si, 4));
    g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(this.sw, 4));

    const all = [];
    let start = 0;
    for (let m = 0; m < this.tris.length; m++) {
      const t = this.tris[m];
      for (let i = 0; i < t.length; i++) all.push(t[i]);
      g.addGroup(start, t.length, m);
      start += t.length;
    }
    g.setIndex(all);
    // Animation pushes limbs well outside the bind-pose bounds; a hand-set
    // sphere avoids both a per-frame recompute and pop-out culling.
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0.95, 0), 1.55);
    g.boundingBox = new THREE.Box3(new THREE.Vector3(-0.9, -0.2, -1.1), new THREE.Vector3(0.9, 2.0, 1.1));
    return g;
  }
}

// ---------------------------------------------------------------------------
// Bind pose
// ---------------------------------------------------------------------------

/** Fresh bone hierarchy. Cheap enough to build one per enemy (20 Object3Ds). */
export function createBones() {
  const bones = new Array(BONE_COUNT);
  for (let i = 0; i < BONE_COUNT; i++) {
    const [name, parent, x, y, z] = BONE_DEFS[i];
    const b = new THREE.Bone();
    b.name = name;
    b.position.set(x, y, z);
    bones[i] = b;
    if (parent !== null) bones[BONE[parent]].add(b);
  }
  return bones;
}

/**
 * Analytic two-bone IK. `S` is the chain root in world space, `T` the target;
 * both bones point down their own local -Y. `pole` is the world direction the
 * middle joint should bulge toward. Writes local quaternions into the bones.
 */
const _ikAim = new THREE.Vector3();
const _ikAxis = new THREE.Vector3();
const _ikUp = new THREE.Vector3();
const _ikX = new THREE.Vector3();
const _ikY = new THREE.Vector3();
const _ikZ = new THREE.Vector3();
const _ikM = new THREE.Matrix4();
const _ikQ = new THREE.Quaternion();
const _ikPQ = new THREE.Quaternion();
const _ikScale = new THREE.Vector3();
const _ikPos = new THREE.Vector3();
const X_AXIS = new THREE.Vector3(1, 0, 0);

export function solveTwoBone(upper, lower, S, T, l1, l2, pole) {
  _ikAim.copy(T).sub(S);
  let d = _ikAim.length();
  if (d < 1e-4) return;
  _ikAim.multiplyScalar(1 / d);
  // Leave a sliver of bend in at full extension — a locked-straight arm reads
  // as a mannequin, and the acos below goes singular at exactly l1+l2.
  d = clamp(d, Math.abs(l1 - l2) + 0.02, (l1 + l2) * 0.995);

  const a = Math.acos(clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1));
  const a2 = Math.acos(clamp((l2 * l2 + d * d - l1 * l1) / (2 * l2 * d), -1, 1));

  _ikUp.copy(pole);
  _ikAxis.crossVectors(_ikAim, _ikUp);
  if (_ikAxis.lengthSq() < 1e-8) {
    _ikUp.set(0, 1, 0);
    _ikAxis.crossVectors(_ikAim, _ikUp);
    if (_ikAxis.lengthSq() < 1e-8) _ikAxis.set(1, 0, 0);
  }
  _ikAxis.normalize();

  // Upper bone direction: the aim rotated toward the pole by the triangle's
  // root angle. Rotating about cross(aim, pole) always bends toward the pole.
  _ikY.copy(_ikAim).applyAxisAngle(_ikAxis, a).multiplyScalar(-1); // local -Y maps to the limb
  _ikX.copy(_ikAxis);
  _ikZ.crossVectors(_ikX, _ikY).normalize();
  _ikX.crossVectors(_ikY, _ikZ).normalize();
  _ikM.makeBasis(_ikX, _ikY, _ikZ);
  _ikQ.setFromRotationMatrix(_ikM);

  upper.parent.matrixWorld.decompose(_ikPos, _ikPQ, _ikScale);
  upper.quaternion.copy(_ikPQ.invert()).multiply(_ikQ);
  // The forearm closes the triangle: exactly -(a + a2) about the bend axis,
  // which in the upper bone's local frame is its own +X.
  lower.quaternion.setFromAxisAngle(X_AXIS, -(a + a2));
}

const POLE_L = new THREE.Vector3(0.60, -0.72, 0.34).normalize();
const POLE_R = new THREE.Vector3(-0.60, -0.72, 0.34).normalize();
export { POLE_L, POLE_R };

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const box = (w, h, d) => new RoundedBoxGeometry(w, h, d, 2, Math.min(w, h, d) * 0.16);
const cyl = (rt, rb, h, s) => new THREE.CylinderGeometry(rt, rb, h, Math.max(12, s));
const sph = (r, w, h, ps, pl, ts, tl) => new THREE.SphereGeometry(r, Math.max(16, w), Math.max(12, h), ps, pl, ts, tl);
const cap = (r, h) => new THREE.CapsuleGeometry(r, h, 5, 12);

function soft(bone, y, span) { return { bone: BONE[bone], y, span }; }

/**
 * The part list. `bone` names the driving joint, `at/rot/scale` place the
 * primitive inside that bone's local frame, `soft` lists the neighbouring
 * joints the surrounding vertices should partly follow.
 */
function soldierParts(P, variant) {
  const parts = [];
  const add = (geo, bone, mat, tint, at, rot, scale, softList) => {
    parts.push({ geo, bone: BONE[bone], mat, tint, at, rot, scale, soft: softList });
  };

  // --- pelvis / belt line ---------------------------------------------------
  add(cyl(0.176, 0.158, 0.20, 10), 'hips', MAT_CLOTH, P.uniform, [0, -0.07, 0], null, [1, 1, 0.72],
    [soft('spine', 0.05, 0.13)]);
  add(cyl(0.187, 0.184, 0.062, 10), 'hips', MAT_KIT, P.gear, [0, 0.032, 0], null, [1, 1, 0.76]);
  add(box(0.052, 0.05, 0.03), 'hips', MAT_HARD, P.dark, [0, 0.032, -0.145], null, null); // buckle
  add(box(0.11, 0.155, 0.085), 'hips', MAT_CLOTH, P.pouch, [-0.155, -0.15, 0.01], [0, 0, 0.12], null);
  add(cyl(0.046, 0.046, 0.14, 8), 'hips', MAT_CLOTH, P.pouch, [0.152, -0.12, 0.085], null, null);

  // --- abdomen / chest ------------------------------------------------------
  // Continuous undershirt closes the old gap between the belt and abdomen.
  add(sph(0.16, 20, 14), 'spine', MAT_CLOTH, P.uniform, [0, -0.045, 0], null, [1.02, 1.14, 0.70],
    [soft('hips', -0.12, 0.12)]);
  add(cyl(0.170, 0.160, 0.215, 10), 'spine', MAT_CLOTH, P.uniform, [0, 0.09, 0], null, [1, 1, 0.66],
    [soft('hips', -0.02, 0.12), soft('chest', 0.21, 0.12)]);
  add(cyl(0.194, 0.172, 0.24, 10), 'chest', MAT_CLOTH, P.uniform, [0, 0.10, 0], null, [1, 1, 0.68],
    [soft('spine', -0.01, 0.12)]);

  // plate carrier — the piece that makes the silhouette read as military
  add(cyl(0.206, 0.198, 0.30, 10), 'chest', MAT_KIT, P.gear, [0, 0.075, 0], null, [1, 1, 0.80],
    [soft('spine', -0.06, 0.10)]);
  add(box(0.275, 0.30, 0.036), 'chest', MAT_KIT, P.gear, [0, 0.085, -0.140], null, null);
  add(box(0.275, 0.30, 0.036), 'chest', MAT_KIT, P.gear, [0, 0.085, 0.140], null, null);
  for (let i = -1; i <= 1; i++) {
    add(box(0.074, 0.115, 0.062), 'chest', MAT_CLOTH, P.pouch, [i * 0.085, 0.005, -0.178], null, null);
  }
  add(box(0.15, 0.09, 0.055), 'chest', MAT_CLOTH, P.pouch, [0, -0.055, 0.175], null, null);
  add(box(0.075, 0.125, 0.06), 'chest', MAT_HARD, P.dark, [0.115, 0.105, 0.155], null, null); // radio
  add(cyl(0.006, 0.004, 0.32, 5), 'chest', MAT_HARD, P.dark, [0.128, 0.31, 0.175], [-0.16, 0, -0.13], null);
  for (const s of [1, -1]) {
    add(box(0.062, 0.30, 0.055), 'chest', MAT_KIT, P.gear, [s * 0.104, 0.115, -0.062], [0, 0, s * 0.09], null,
      [soft(s > 0 ? 'clavL' : 'clavR', 0.20, 0.12)]);
  }
  add(cyl(0.118, 0.112, 0.075, 10), 'chest', MAT_CLOTH, P.uniform, [0, 0.225, 0], null, [1, 1, 0.88],
    [soft('neck', 0.26, 0.09)]);

  // --- neck / head ----------------------------------------------------------
  add(cyl(0.050, 0.056, 0.115, 8), 'neck', MAT_CLOTH, P.skin, [0, 0.05, 0], null, null,
    [soft('chest', -0.02, 0.08), soft('head', 0.10, 0.07)]);
  add(sph(0.098, 10, 8), 'head', MAT_CLOTH, P.skin, [0, 0.085, 0.005], null, [1, 1.10, 1.08]);
  add(sph(0.076, 8, 6), 'head', MAT_CLOTH, P.dark, [0, 0.048, -0.048], null, [0.96, 0.72, 1.0]); // balaclava
  add(sph(0.129, 12, 8, 0, TAU, 0, 1.78), 'head', MAT_KIT, P.helmet, [0, 0.088, 0.008], null, [1, 0.99, 1.06]);
  add(cyl(0.133, 0.129, 0.032, 12), 'head', MAT_KIT, P.helmet, [0, 0.052, 0.008], null, [1, 1, 1.06]);
  add(cyl(0.132, 0.132, 0.030, 12), 'head', MAT_CLOTH, P.dark, [0, 0.098, 0.006], null, [1, 1, 1.07]); // goggle strap
  for (const s of [1, -1]) {
    add(cyl(0.043, 0.043, 0.036, 8), 'head', MAT_HARD, P.dark, [s * 0.112, 0.062, 0.012], [0, 0, Math.PI / 2], null);
  }
  add(box(0.185, 0.052, 0.038), 'head', MAT_HARD, P.visor, [0, 0.098, -0.092], [0.10, 0, 0], null);
  if (variant !== 1) add(box(0.056, 0.046, 0.032), 'head', MAT_HARD, P.dark, [0, 0.128, -0.112], null, null); // NVG mount

  // --- arms -----------------------------------------------------------------
  // Equipment is modeled into the shared skin: no extra draw calls per actor.
  // Different loadouts change silhouette as well as uniform colour.
  for (let row = 0; row < 3; row++) {
    for (let col = -2; col <= 2; col++) {
      add(box(0.043, 0.011, 0.009), 'chest', MAT_CLOTH, P.pouch,
        [col * 0.048, 0.080 + row * 0.034, -0.164]);
    }
  }
  add(box(0.083, 0.026, 0.008), 'chest', MAT_CLOTH, P.dark, [-0.064, 0.195, -0.163]);
  if (variant === 1) {
    add(box(0.22, 0.34, 0.11), 'chest', MAT_CLOTH, P.pouch, [0, 0.02, 0.203]);
    add(box(0.17, 0.13, 0.06), 'chest', MAT_CLOTH, P.gear, [0, -0.06, 0.272]);
  } else if (variant === 2) {
    add(box(0.105, 0.17, 0.06), 'hips', MAT_KIT, P.dark, [-0.178, -0.19, -0.025]);
  }
  for (const s of [-1, 1]) {
    const L = s > 0 ? 'L' : 'R';
    add(box(0.065, 0.11, 0.065), 'head', MAT_KIT, P.dark, [s * 0.119, 0.06, 0.014]);
    add(box(0.012, 0.10, 0.016), 'head', MAT_CLOTH, P.glove, [s * 0.076, 0.012, -0.054], [0.2, 0, -s * 0.35]);
    add(box(0.066, 0.12, 0.075), `thigh${L}`, MAT_CLOTH, P.pouch, [s * 0.08, -0.18, 0.008]);
    // Boot laces and glove knuckles give close views physical scale.
    for (let i = 0; i < 4; i++) {
      add(box(0.061, 0.004, 0.007), `foot${L}`, MAT_CLOTH, P.glove, [0, 0.031, -0.07 - i * 0.023]);
      add(sph(0.011, 12, 8), `hand${L}`, MAT_KIT, P.dark, [-0.024 + i * 0.016, -0.056, -0.048], null, [0.65, 1, 0.5]);
    }
  }
  // Eyewear has a rubber surround and two recessed lenses, not a glowing bar.
  add(box(0.188, 0.060, 0.014), 'head', MAT_KIT, P.dark, [0, 0.098, -0.113]);
  for (const s of [-1, 1]) {
    add(box(0.074, 0.037, 0.009), 'head', MAT_HARD, P.visor, [s * 0.045, 0.098, -0.124], [0, s * 0.12, 0]);
  }

  for (const s of [1, -1]) {
    const L = s > 0 ? 'L' : 'R';
    add(sph(0.084, 8, 6), `arm${L}`, MAT_CLOTH, P.uniform, [0, 0.006, 0], null, [1, 1, 0.95],
      [soft(`clav${L}`, 0.02, 0.13)]);
    add(box(0.115, 0.085, 0.145), `arm${L}`, MAT_KIT, P.gear, [s * 0.012, -0.012, 0], [0, 0, s * 0.12], null,
      [soft(`clav${L}`, 0.02, 0.12)]);
    add(cap(0.058, 0.215), `arm${L}`, MAT_CLOTH, P.uniform, [0, -0.145, 0], null, null,
      [soft(`clav${L}`, -0.01, 0.10), soft(`fore${L}`, -0.29, 0.10)]);
    add(sph(0.058, 8, 6), `arm${L}`, MAT_KIT, P.gear, [0, -0.288, -0.012], null, [1, 1, 1],
      [soft(`fore${L}`, -0.29, 0.09)]);
    add(cap(0.050, 0.185), `fore${L}`, MAT_CLOTH, P.uniform, [0, -0.135, 0], null, null,
      [soft(`arm${L}`, -0.005, 0.09), soft(`hand${L}`, -0.27, 0.08)]);
    add(cyl(0.057, 0.050, 0.055, 8), `fore${L}`, MAT_CLOTH, P.glove, [0, -0.248, 0], null, null,
      [soft(`hand${L}`, -0.27, 0.06)]);
    add(box(0.064, 0.098, 0.088), `hand${L}`, MAT_CLOTH, P.glove, [0, -0.046, -0.008], null, null,
      [soft(`fore${L}`, 0.005, 0.06)]);
    add(cyl(0.021, 0.018, 0.058, 6), `hand${L}`, MAT_CLOTH, P.glove, [-s * 0.032, -0.030, -0.030],
      [0, 0, s * 0.65], null);
  }

  // --- legs -----------------------------------------------------------------
  for (const s of [1, -1]) {
    const L = s > 0 ? 'L' : 'R';
    add(cap(0.094, 0.28), `thigh${L}`, MAT_CLOTH, P.uniform, [0, -0.205, 0.004], null, null,
      [soft('hips', -0.02, 0.13), soft(`shin${L}`, -0.44, 0.11)]);
    add(box(0.108, 0.115, 0.052), `thigh${L}`, MAT_KIT, P.gear, [0, -0.412, -0.078], [0.12, 0, 0], null,
      [soft(`shin${L}`, -0.44, 0.10)]);
    add(cap(0.072, 0.275), `shin${L}`, MAT_CLOTH, P.uniform, [0, -0.195, 0.002], null, null,
      [soft(`thigh${L}`, -0.005, 0.09), soft(`foot${L}`, -0.42, 0.10)]);
    add(box(0.100, 0.115, 0.135), `foot${L}`, MAT_CLOTH, P.boot, [0, 0.006, -0.012], null, null,
      [soft(`shin${L}`, 0.02, 0.08)]);
    add(box(0.098, 0.078, 0.165), `foot${L}`, MAT_CLOTH, P.boot, [0, -0.014, -0.105], null, null);
    add(box(0.106, 0.032, 0.285), `foot${L}`, MAT_HARD, P.sole, [0, -0.044, -0.058], null, null);
  }

  // --- carbine --------------------------------------------------------------
  add(box(0.052, 0.086, 0.28), 'weapon', MAT_HARD, P.gun, [0, 0, -0.01], null, null);
  add(box(0.042, 0.022, 0.34), 'weapon', MAT_HARD, P.gun, [0, 0.053, -0.10], null, null);
  add(box(0.049, 0.058, 0.225), 'weapon', MAT_HARD, P.gun, [0, 0.010, -0.262], null, null);
  add(cyl(0.011, 0.010, 0.16, 8), 'weapon', MAT_HARD, P.dark, [0, 0.012, -0.42], [Math.PI / 2, 0, 0], null);
  add(cyl(0.018, 0.016, 0.055, 8), 'weapon', MAT_HARD, P.dark, [0, 0.012, -0.482], [Math.PI / 2, 0, 0], null);
  add(box(0.045, 0.074, 0.20), 'weapon', MAT_KIT, P.gun, [0, -0.006, 0.215], null, null);
  add(cyl(0.023, 0.023, 0.11, 8), 'weapon', MAT_HARD, P.gun, [0, 0.004, 0.15], [Math.PI / 2, 0, 0], null);
  add(box(0.034, 0.108, 0.05), 'weapon', MAT_HARD, P.grip, [0, -0.074, 0.052], [0.30, 0, 0], null);
  add(box(0.031, 0.15, 0.074), 'weapon', MAT_KIT, P.dark, [0, -0.098, -0.046], [-0.13, 0, 0], null);
  add(box(0.031, 0.095, 0.070), 'weapon', MAT_KIT, P.dark, [0, -0.196, -0.020], [-0.34, 0, 0], null);
  add(box(0.039, 0.044, 0.098), 'weapon', MAT_HARD, P.gun, [0, 0.088, -0.072], null, null);
  add(box(0.033, 0.032, 0.062), 'weapon', MAT_HARD, P.dark, [0, 0.063, -0.072], null, null);
  add(box(0.030, 0.078, 0.034), 'weapon', MAT_HARD, P.grip, [0, -0.042, -0.29], [-0.10, 0, 0], null);

  return parts;
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

/**
 * `rubber` is not in the shared Materials palette, so grips and boot soles ride
 * on gunMetal with a matte tint instead. See NOTES-a5.md.
 *
 * Soldiers ask for their own repeat/size so they get their own texture objects:
 * the level's copies tile 1:1 across a whole wall, which on a 7 cm pouch would
 * be one smeared texel. 256 px is plenty at character scale and keeps the boot
 * cost of nine extra canvas bakes down.
 */
function safeMat(name, fallback) {
  const opts = { clone: true, repeat: 4, size: 256, overrides: { vertexColors: true, envMapIntensity: 0.55 } };
  try { return Materials.get(name, opts); }
  catch { return Materials.get(fallback, opts); }
}

let _assets = null;

export function buildSoldierAssets(quality = 'high') {
  if (_assets) return _assets;

  const materials = [
    safeMat('fabric', 'fabric'),
    safeMat('metalPainted', 'fabric'),
    safeMat('gunMetal', 'metalPainted'),
  ];
  materials.forEach((m, i) => {
    // Fabric and polymer equipment should not reflect like sheet metal.
    m.metalness = i === MAT_HARD ? 0.65 : 0;
    m.roughness = i === MAT_HARD ? 0.48 : i === MAT_KIT ? 0.86 : 0.97;
    m.name = `soldier:${i}`;
    m.needsUpdate = true;
  });

  // Reference rig, posed once, used to bake the bind pose for every variant.
  const bones = createBones();
  const root = bones[0];
  applyBindPose(bones);
  root.updateMatrixWorld(true);

  const boneInverses = [];
  for (let i = 0; i < BONE_COUNT; i++) {
    boneInverses.push(new THREE.Matrix4().copy(bones[i].matrixWorld).invert());
  }

  const variants = [];
  const nVariants = quality === 'low' ? 2 : 3;
  for (let v = 0; v < nVariants; v++) {
    const acc = new GeoAcc(3);
    const parts = soldierParts(PALETTES[v], v);
    for (const p of parts) {
      _m4b.identity();
      if (p.rot) _q.setFromEuler(_e.set(p.rot[0], p.rot[1], p.rot[2], 'XYZ'));
      else _q.identity();
      _m4b.compose(
        _v.set(p.at ? p.at[0] : 0, p.at ? p.at[1] : 0, p.at ? p.at[2] : 0),
        _q,
        _n.set(p.scale ? p.scale[0] : 1, p.scale ? p.scale[1] : 1, p.scale ? p.scale[2] : 1),
      );
      acc.add(p.geo, _m4b, bones[p.bone].matrixWorld, p.bone, p.soft, p.mat, p.tint);
      p.geo.dispose();
    }
    variants.push(acc.build());
  }

  _assets = { materials, variants, boneInverses };
  return _assets;
}

/**
 * The bind pose is the weapon-ready stance rather than a T-pose: skin weights
 * are least distorted near the pose they were authored in, and this is the pose
 * an enemy spends most of its life in.
 */
export function applyBindPose(bones) {
  const chest = bones[BONE.chest];
  const weapon = bones[BONE.weapon];
  weapon.position.set(-0.085, 0.03, -0.20);
  weapon.quaternion.identity();
  bones[BONE.hips].updateMatrixWorld(true);
  ikHandsToWeapon(bones);
  bones[BONE.hips].updateMatrixWorld(true);
  void chest;
}

const _gripW = new THREE.Vector3();
const _shoulderW = new THREE.Vector3();
const _poleW = new THREE.Vector3();
const _upW = new THREE.Vector3();

/** Welds both hands to the carbine's grips using the current bone world matrices. */
export function ikHandsToWeapon(bones) {
  const weapon = bones[BONE.weapon];
  const wm = weapon.matrixWorld;

  // support hand rides a little above the handguard so the palm, not the wrist,
  // lands on it
  _upW.set(0, 1, 0).transformDirection(wm);

  _gripW.copy(WEAPON_LOCAL.gripFront).applyMatrix4(wm).addScaledVector(_upW, 0.055);
  _shoulderW.setFromMatrixPosition(bones[BONE.armL].matrixWorld);
  _poleW.copy(POLE_L).transformDirection(bones[BONE.chest].matrixWorld);
  solveTwoBone(bones[BONE.armL], bones[BONE.foreL], _shoulderW, _gripW, ARM.upper, ARM.fore, _poleW);

  _gripW.copy(WEAPON_LOCAL.gripRear).applyMatrix4(wm).addScaledVector(_upW, 0.05);
  _shoulderW.setFromMatrixPosition(bones[BONE.armR].matrixWorld);
  _poleW.copy(POLE_R).transformDirection(bones[BONE.chest].matrixWorld);
  solveTwoBone(bones[BONE.armR], bones[BONE.foreR], _shoulderW, _gripW, ARM.upper, ARM.fore, _poleW);

  // Wrists: point the glove along the weapon instead of dangling off the arm.
  bones[BONE.handL].quaternion.set(0.30, 0, 0, 0.954).normalize();
  bones[BONE.handR].quaternion.set(0.24, 0, 0, 0.971).normalize();
}

/** Hitbox volumes. Head is deliberately tiny — 0.13 m radius on a 1.85 m body. */
export const HITBOX_DEFS = [
  { bone: 'head', zone: 'head', shape: 'sphere', r: 0.132, at: [0, 0.088, 0.0] },
  { bone: 'chest', zone: 'torso', shape: 'box', size: [0.44, 0.40, 0.32], at: [0, 0.10, 0] },
  { bone: 'hips', zone: 'torso', shape: 'box', size: [0.40, 0.34, 0.30], at: [0, 0.03, 0] },
  { bone: 'armL', zone: 'limb', shape: 'box', size: [0.17, 0.60, 0.19], at: [0, -0.28, 0] },
  { bone: 'armR', zone: 'limb', shape: 'box', size: [0.17, 0.60, 0.19], at: [0, -0.28, 0] },
  { bone: 'thighL', zone: 'limb', shape: 'box', size: [0.22, 0.92, 0.26], at: [0, -0.44, 0] },
  { bone: 'thighR', zone: 'limb', shape: 'box', size: [0.22, 0.92, 0.26], at: [0, -0.44, 0] },
];

let _hitGeo = null;
let _hitMat = null;
export function hitboxAssets() {
  if (!_hitGeo) {
    _hitGeo = {
      box: new THREE.BoxGeometry(1, 1, 1),
      sphere: new THREE.SphereGeometry(1, 8, 6),
    };
    _hitMat = new THREE.MeshBasicMaterial({ visible: false });
  }
  return { geo: _hitGeo, mat: _hitMat };
}
